import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import trI18n from '@/common/utils/trI18n';
import Utils from '@/common/utils/utils';
import { Tooltip } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import ConfirmDialog from '@/common/dialog/ConfirmDialog';
import { useRecoilState, useRecoilValue } from 'recoil';
import { bulkActionHelpdeskTaskIdAtom } from '@/state/atom/ticketKnowledgeAtom';
import BulkActionDropdownHelpDesk from './BulkActionDropdownHelpDesk';
import Constant from '@/common/constant/Constant';
import {
    handleBulkUpdateTicketAPI,
    handleMsgDialogConfirmByBulkAction,
    handleMsgPendingWhenBulkActionInProcessing,
    handleTxtSubmitAndCancelActionPopup
} from '@/component/ticketKnowledge/handler/ListTicketKnowledgeHandler';
import { handleOpenLogScreen } from '@/component/ticketKnowledge/handler/KnowledgeUploadCsvHandler';
import { projectInuseDetailAtom } from '@/state/atom/projectAtom';
import { tr } from '@/common/utils/tr';
import { useTranslationSubscription } from '@/hooks/useTranslation';
import { buildSnackbarMessage } from '@/component/common/SnackbarMessage';

function BulkActionHelpDesk({
    listTicketHelpDesk = [],
    totalTickets = 0,
    viewSelected,
    selectedProject,
    onSubmitSearchListHelpDesk,
    currentSearchParams,
    drillDownData,
    viewStructure,
    setIsShowDialogNotiLimit,
    msgInProcessingIcon,
    setMsgInProcessingIcon,
    setIsShowEditTagField,
    setIsShowExportMenu,
    isOpenDialogConfirm,
    setIsOpenDialogConfirm
}) {
    // Re-render when the translation dictionary loads/extends so tr() text updates.
    useTranslationSubscription();

    const location = useLocation();
    // Atom
    const { enqueueSnackbar } = useSnackbar();
    const [helpDeskTaskId, setHelpDeskTaskId] = useRecoilState(bulkActionHelpdeskTaskIdAtom);
    const projectInuseDetail = useRecoilValue(projectInuseDetailAtom);

    const [bulkActionList, setBulkActionList] = useState([]);
    const [disableButtonCallBack, setDisableButtonCallBack] = useState(false);
    const [errorDialog, setErrorDialog] = useState({
        enable: false,
        msg: '',
    });

    const isDisplayHelpdeskTicketList = () => {
        if (Utils.isEmptyObject(projectInuseDetail)) {
            return false;
        }
        
        // If prj inuse is base or guest customer => return false
        if (
            [Constant.PROJECT_TYPE.BASE, Constant.PROJECT_TYPE.CUSTOMER].includes(
                projectInuseDetail?.type
            ) ||
            Utils.isProjectGuest(projectInuseDetail)
        ) {
            return false;
        }

        return true;
    };

    const validateDisableForActionList = (actionList, isLiveAndPublicView) => {
        actionList = actionList.map(action => {
            //export to csv
            if (action.type === Constant.BULK_ACTION.HELPDESK_EXPORT_CSV) {
                const fields = !Utils.isEmptyArray(viewStructure?.fields) ? viewStructure?.fields : [];
                const indexOfSelectedForm = fields?.find(field => {
                    return !Utils.isEmptyString(field?.form)
                });
                action.disable = !indexOfSelectedForm;
            }
            return action;
        });
        return actionList;
    }

    // handle bulkActionList option
    useEffect(() => {
        if (!Utils.isEmptyObject(viewSelected)) {
            const structure = Utils.convertJSONDataToObject(viewSelected.structure);
            const ticketProperty = structure?.ticketProperty;
            let initBulkActionList = Constant.HELPDESK_BULK_ACTION_LIST;
            let isLiveAndPublicView = false;

            switch (ticketProperty) {
                case Constant.TICKET_PROPERTY.SENT:
                    isLiveAndPublicView = true;
                    initBulkActionList = initBulkActionList.filter(action => {
                        return ![Constant.BULK_ACTION.HELPDESK_RESTORE, Constant.BULK_ACTION.HELPDESK_BACK_TO_NEW_SUPPORT].includes(action.type);
                    });
                    break;
                case Constant.TICKET_PROPERTY.DRAFT:
                    initBulkActionList = initBulkActionList.filter(action => {
                        return ![Constant.BULK_ACTION.HELPDESK_RESTORE, Constant.BULK_ACTION.HELPDESK_BACK_TO_NEW_SUPPORT, Constant.BULK_ACTION.HELPDESK_ARCHIVE].includes(action.type);
                    });
                    break;
                case Constant.TICKET_PROPERTY.DELETE:
                    initBulkActionList = initBulkActionList
                        .filter(action => {
                            return [Constant.BULK_ACTION.HELPDESK_DELETE, Constant.BULK_ACTION.HELPDESK_RESTORE].includes(action.type);
                        })
                        .map(action => {
                            if (action.type === Constant.BULK_ACTION.HELPDESK_DELETE) {
                                return {
                                    ...action,
                                    type: Constant.BULK_ACTION.HELPDESK_HARD_DELETE
                                }
                            }
                            return action
                        })
                    break;
                case Constant.TICKET_PROPERTY.ARCHIVE:
                    initBulkActionList = initBulkActionList.filter(action => {
                        return [
                            Constant.BULK_ACTION.HELPDESK_DELETE,
                            Constant.BULK_ACTION.HELPDESK_BACK_TO_NEW_SUPPORT,
                            Constant.BULK_ACTION.HELPDESK_EXPORT_CSV,
                            Constant.BULK_ACTION.HELPDESK_EDIT_TAG_FIELD
                        ].includes(action.type);
                    });
                    break;
                case Constant.TICKET_PROPERTY.TRAINING:
                    initBulkActionList = initBulkActionList.filter(action => {
                        return ![
                            Constant.BULK_ACTION.HELPDESK_RESTORE,
                            Constant.BULK_ACTION.HELPDESK_BACK_TO_NEW_SUPPORT,
                        ].includes(action.type);
                    });
                    break;
                default:
                    break;
            }

            if (!selectedProject.isDesk && totalTickets <= 0) {
                initBulkActionList = initBulkActionList.filter(action => {
                    return ![Constant.BULK_ACTION.HELPDESK_BACK_TO_NEW_SUPPORT, Constant.BULK_ACTION.HELPDESK_ARCHIVE].includes(action.type);
                });
            }

            initBulkActionList = validateDisableForActionList(initBulkActionList, isLiveAndPublicView)

            setBulkActionList(initBulkActionList);
        }
    }, [viewSelected, viewStructure, drillDownData, listTicketHelpDesk]);

    const handleAction = async (actionType) => {
        let params = {
            searchCondition: JSON.stringify({
                ...currentSearchParams,
                userAffiliationOrganization: currentSearchParams.corporationId,
                projectInuse: currentSearchParams.projectId
            }),
        };
        const structure = Utils.convertJSONDataToObject(viewSelected?.structure);
        const currentViewType = structure?.ticketProperty;
        setIsShowEditTagField(false);
        setIsShowExportMenu(false)
        switch (actionType) {
            case Constant.BULK_ACTION.HELPDESK_EDIT_TAG_FIELD:
                setIsShowEditTagField(true);
                return;
            case Constant.BULK_ACTION.HELPDESK_EXPORT_CSV:
                if (currentViewType === Constant.TICKET_PROPERTY.DELETE) {
                    return;
                }
                params.action = Constant.BULK_ACTION.HELPDESK_EXPORT_CSV;
                setIsOpenDialogConfirm({
                    status: false,
                    action: params.action,
                    params: { ...params },
                });
                setIsShowExportMenu(true)
                return
            case Constant.BULK_ACTION.HELPDESK_DELETE:
            case Constant.BULK_ACTION.HELPDESK_HARD_DELETE:
                params.action = (currentViewType === Constant.TICKET_PROPERTY.DELETE) ? Constant.BULK_ACTION.HELPDESK_HARD_DELETE : Constant.BULK_ACTION.HELPDESK_SOFT_DELETE;
                break;
            default:
                params.action = actionType; 
                break;
        }
        setIsOpenDialogConfirm({
            status: true,
            action: params.action,
            params: { ...params },
        });
    };

    const isDisableBulkBtn = () => {
        return (totalTickets == 0 || !isDisplayHelpdeskTicketList());
    }

    return (
        <>
            <div className="bulk-action" id='help-desk-bulk-actions'>
                <BulkActionDropdownHelpDesk
                    bulkActionList={bulkActionList}
                    handleAction={handleAction}
                    disableBulkBtn={isDisableBulkBtn()}
                    pendingBulkBtn={!Utils.isEmptyString(helpDeskTaskId)}
                />
                <img
                    className="option-icon"
                    src="/assets/image/process-upload-csv.svg"
                    alt="icon-view-log"
                    onClick={() => handleOpenLogScreen(`${location.pathname}/log-screen/bulk-actions/customer-support`)}
                />
                <img
                    className="option-icon"
                    src={`/assets/image/icon-reload.svg`}
                    alt="icon-reload"
                    onClick={() => onSubmitSearchListHelpDesk()}
                />
                {!Utils.isEmptyString(helpDeskTaskId) && (
                    <Tooltip title={trI18n(msgInProcessingIcon)} arrow placement="right-end">
                        <img
                            className="option-icon icon-loading"
                            src={`/assets/image/icon-loading.svg`}
                            alt="icon-loading"
                        />
                    </Tooltip>
                )}
            </div>

            <ConfirmDialog
                isOpen={isOpenDialogConfirm.status}
                msgConfirm={tr(
                    trI18n(handleMsgDialogConfirmByBulkAction(isOpenDialogConfirm.action)).replace('[number]', totalTickets)
                )}
                closeCallBack={(e, reason) => {
                    if (e && reason === 'backdropClick') return false;
                    setIsOpenDialogConfirm({
                        status: false,
                        action: '',
                        params: {}
                    });
                    return;
                }}
                okCallBack={() => {
                    if (totalTickets > Constant.LIMIT_TICKET_UPDATE_MULTI && isOpenDialogConfirm.action !== Constant.BULK_ACTION.HELPDESK_EXPORT_CSV) {
                        setIsOpenDialogConfirm({
                            status: false,
                            action: '',
                            params: {}
                        });
                        setIsShowDialogNotiLimit(true);
                        return;
                    }

                    if (
                        totalTickets > Constant.LIMIT_TICKET_EXPORT &&
                        isOpenDialogConfirm.action === Constant.BULK_ACTION.HELPDESK_EXPORT_CSV
                    ) {
                        return enqueueSnackbar(
                            buildSnackbarMessage('MSG-CM-OVER-SIZE-FILE-ERROR-WHEN-EXPORT-CSV'),
                            {
                                variant: 'error',
                                autoHideDuration: 3000
                            }
                        );
                    }

                    setDisableButtonCallBack(true);
                    setIsShowExportMenu(false)
                    setMsgInProcessingIcon(handleMsgPendingWhenBulkActionInProcessing(isOpenDialogConfirm.action));
                    document.body.style.cursor = 'wait';
                    handleBulkUpdateTicketAPI(
                        isOpenDialogConfirm,
                        setIsOpenDialogConfirm,
                        setHelpDeskTaskId,
                        setDisableButtonCallBack,
                        enqueueSnackbar
                    );
                    return;
                }}
                textSubmit={trI18n(handleTxtSubmitAndCancelActionPopup(isOpenDialogConfirm.action)?.submit)}
                textClose={trI18n(handleTxtSubmitAndCancelActionPopup(isOpenDialogConfirm.action)?.cancel)}
                disableButtonCallBack={disableButtonCallBack}
            />

            <ConfirmDialog
                isOpen={errorDialog.enable}
                msgConfirm={trI18n(errorDialog.msg)}
                okCallBack={() => {
                    setMsgInProcessingIcon('')
                    setErrorDialog({ enable: false, msg: '' })
                }}
            />
        </>
    );
}

export default BulkActionHelpDesk;
